import { useRef, useState, type DragEvent } from "react";
import { Camera, ImagePlus, Loader2, RotateCw, Upload, X } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { useTranslation } from "@/shared/i18n";
import { cn } from "@/lib/utils";

interface Props {
  previewUrl?: string;
  fileName: string;
  onCapture: () => void;
  onUpload: (file: File) => void;
  onClear: () => void;
  capturing?: boolean;
}

export function PreviewImagePanel({
  previewUrl,
  fileName,
  onCapture,
  onUpload,
  onClear,
  capturing,
}: Props) {
  const { t } = useTranslation("psdTemplates");
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const isFilled = Boolean(previewUrl);

  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith("image/"));
    if (file) onUpload(file);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{t("preview.title")}</h3>
        <span
          className={cn(
            "text-[10px] font-mono px-1.5 py-0.5 rounded-md border",
            isFilled
              ? "border-primary/40 text-primary bg-primary/5"
              : "border-border/60 text-muted-foreground bg-muted/30",
          )}
        >
          {fileName}
        </span>
      </div>
      <p className="text-xs text-muted-foreground -mt-2">{t("preview.hint")}</p>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={cn(
          "group/preview relative aspect-[4/5] w-full rounded-xl overflow-hidden transition-all",
          isFilled
            ? "shadow-sm hover:shadow-md ring-1 ring-border hover:ring-primary/40"
            : "bg-gradient-to-br from-muted/40 to-muted/70 border-2 border-dashed border-primary/30 hover:border-primary/70 hover:bg-muted/60",
          dragging && "ring-2 ring-primary border-primary bg-primary/5",
        )}
        title={isFilled ? t("preview.upload") : t("preview.empty")}
      >
        {isFilled ? (
          <>
            <img
              src={previewUrl}
              alt={fileName}
              className="w-full h-full object-contain bg-black/30"
              draggable={false}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/30 opacity-0 group-hover/preview:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white text-[11px] font-medium flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-full">
                <RotateCw className="w-3 h-3" />
                {t("preview.upload")}
              </span>
            </div>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onClear();
                }
              }}
              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-background/85 hover:bg-background border flex items-center justify-center text-foreground opacity-0 group-hover/preview:opacity-100 transition-opacity cursor-pointer"
              aria-label={t("preview.clear")}
            >
              <X className="w-3 h-3" />
            </span>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground group-hover/preview:text-primary transition-colors px-4 text-center">
            <div className="w-10 h-10 rounded-full bg-primary/10 group-hover/preview:bg-primary/20 flex items-center justify-center transition-colors">
              <ImagePlus className="w-5 h-5 text-primary" />
            </div>
            <span className="text-xs font-medium">{t("preview.empty")}</span>
          </div>
        )}
      </button>

      <Button
        onClick={onCapture}
        disabled={capturing}
        className="w-full"
        size="sm"
      >
        {capturing ? (
          <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
        ) : (
          <Camera className="w-4 h-4 mr-1.5" />
        )}
        {capturing ? t("preview.capturing") : t("preview.capture")}
      </Button>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="w-full text-[11px] text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5 py-1"
      >
        <Upload className="w-3 h-3" />
        {t("preview.upload")}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}
